// src/components/IngredientUsageReport.js

import React, { useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';

export default function IngredientUsageReport() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  const generateReport = async () => {
    if (!startDate || !endDate) {
      alert('Please select a date range');
      return;
    }

    setLoading(true);

    try {
      const usageQuery = query(
        collection(db, 'shop_stock_audits'),
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );

      const usageSnap = await getDocs(usageQuery);
      const warehouseSnap = await getDocs(collection(db, 'stock_store'));
      const shopSnap = await getDocs(collection(db, 'stock_shop'));

      const warehouseMap = {};
      const shopMap = {};

      warehouseSnap.forEach((docSnap) => {
        warehouseMap[docSnap.id] = docSnap.data();
      });

      shopSnap.forEach((docSnap) => {
        shopMap[docSnap.id] = docSnap.data();
      });

      const usageMap = {};

      usageSnap.forEach((docSnap) => {
        const data = docSnap.data();
        const code = data.stockId;

        if (!code) return;

        if (!usageMap[code]) {
          usageMap[code] = {
            code,
            ingredient: data.ingredient || code,
            usedKg: 0,
          };
        }

        usageMap[code].usedKg += Number(data.requiredKg || 0);
      });

      const result = Object.values(usageMap)
        .map((item) => {
          const warehouseStock = warehouseMap[item.code] || {};
          const shopStock = shopMap[item.code] || {};

          const boxWeight =
            Number(warehouseStock.boxWeight || 0) ||
            Number(shopStock.boxWeight || 0);

          const warehouseKgNow = Number(warehouseStock.totalKg || 0);
          const shopKgNow = Number(shopStock.totalKg || 0);
          const totalKgNow = warehouseKgNow + shopKgNow;

          const usedBoxes = boxWeight > 0 ? item.usedKg / boxWeight : 0;
          const warehouseBoxesNow =
            boxWeight > 0 ? warehouseKgNow / boxWeight : 0;
          const shopBoxesNow =
            boxWeight > 0 ? shopKgNow / boxWeight : 0;
          const totalBoxesNow =
            boxWeight > 0 ? totalKgNow / boxWeight : 0;

          const totalBoxesBefore = totalBoxesNow + usedBoxes;

          const buyAgainBoxes =
            boxWeight > 0 ? Math.ceil(usedBoxes) : 0;

          return {
            ...item,
            boxWeight: round2(boxWeight),
            usedKg: round2(item.usedKg),
            usedBoxes: round2(usedBoxes),

            shopKgNow: round2(shopKgNow),
            shopBoxesNow: round2(shopBoxesNow),

            warehouseKgNow: round2(warehouseKgNow),
            warehouseBoxesNow: round2(warehouseBoxesNow),

            totalKgNow: round2(totalKgNow),
            totalBoxesNow: round2(totalBoxesNow),
            totalBoxesBefore: round2(totalBoxesBefore),

            buyAgainBoxes,
          };
        })
        .sort((a, b) => b.usedBoxes - a.usedBoxes);

      setRows(result);
    } catch (err) {
      console.error(err);
      alert('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!rows.length) return;

    const headers = [
      'Code',
      'Ingredient',
      'Used Boxes',
      'Shop Boxes Now',
      'Warehouse Boxes Now',
      'Total Boxes Now',
      'Total Boxes Before',
      'Buy Again Boxes',
      'Used Kg',
      'Shop Kg Now',
      'Warehouse Kg Now',
      'Total Kg Now',
      'Box Weight Kg',
    ];

    const csvRows = rows.map((r) => [
      r.code,
      r.ingredient,
      r.usedBoxes.toFixed(2),
      r.shopBoxesNow.toFixed(2),
      r.warehouseBoxesNow.toFixed(2),
      r.totalBoxesNow.toFixed(2),
      r.totalBoxesBefore.toFixed(2),
      r.buyAgainBoxes,
      r.usedKg.toFixed(2),
      r.shopKgNow.toFixed(2),
      r.warehouseKgNow.toFixed(2),
      r.totalKgNow.toFixed(2),
      r.boxWeight.toFixed(2),
    ]);

    const csv = [
      headers.join(','),
      ...csvRows.map((row) => row.join(',')),
    ].join('\n');

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `ingredient_usage_${startDate}_to_${endDate}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="ingredient-usage-report">
      <h2>📦 Ingredient Usage Report</h2>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <label>Start Date</label>
          <br />
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div>
          <label>End Date</label>
          <br />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div style={{ alignSelf: 'end' }}>
          <button
            className="btn btn-primary"
            onClick={generateReport}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
        </div>

        {rows.length > 0 && (
          <div style={{ alignSelf: 'end' }}>
            <button className="btn" onClick={exportCSV}>
              📥 Export CSV
            </button>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <table
          border="1"
          cellPadding="8"
          style={{
            width: '100%',
            textAlign: 'center',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr>
              <th>Code</th>
              <th>Ingredient</th>
              <th>Used Boxes</th>
              <th>Shop Boxes Now</th>
              <th>Warehouse Boxes Now</th>
              <th>Total Boxes Now</th>
              <th>Total Boxes Before</th>
              <th>Buy Again Boxes</th>
              <th>Used kg</th>
              <th>Box kg</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr key={r.code}>
                <td>{r.code}</td>
                <td>{r.ingredient}</td>
                <td>{r.usedBoxes.toFixed(2)}</td>
                <td>{r.shopBoxesNow.toFixed(2)}</td>
                <td>{r.warehouseBoxesNow.toFixed(2)}</td>
                <td>{r.totalBoxesNow.toFixed(2)}</td>
                <td>{r.totalBoxesBefore.toFixed(2)}</td>
                <td>
                  <strong>{r.buyAgainBoxes}</strong>
                </td>
                <td>{r.usedKg.toFixed(2)}</td>
                <td>{r.boxWeight.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}